import React, { useState } from 'react';
import { Box, Button, Container, SpaceBetween } from '@amzn/awsui-components-react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import RichTextEditor from './CustomRichTextEditor';
import { getCleanedItemContent, cleanItemContent } from '../CloseHelperMethods';
import { SANITIZE_ALLOWED_TAGS, SANITIZE_ALLOWED_ATTRIBUTES } from '../constants';
import '../closeportal.scss';

function RichTextCommentEditor({ content, onSave, canEdit = false }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [draft, setDraft] = useState('');

    const decodedContent = content ? getCleanedItemContent(content) : '';
    const sanitized = decodedContent
        ? DOMPurify.sanitize(decodedContent, {
              ALLOWED_TAGS: SANITIZE_ALLOWED_TAGS,
              ALLOWED_ATTR: SANITIZE_ALLOWED_ATTRIBUTES,
          })
        : '';

    // Read-only users should only see the comment if there's actual text content
    // Strip HTML tags to check — Quill stores empty content as e.g. <p><br></p>
    const hasVisibleContent = sanitized.replace(/<[^>]*>/g, '').trim().length > 0;
    if (!canEdit && !hasVisibleContent) {
        return null;
    }

    const handleEdit = () => {
        setDraft(decodedContent);
        setSaveError(false);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setDraft('');
        setIsEditing(false);
    };

    const handleSave = async () => {
        const encoded = cleanItemContent(draft);
        setIsSaving(true);
        setSaveError(false);
        try {
            await onSave(encoded);
            setIsEditing(false);
            setDraft('');
        } catch (e) {
            console.error('Save failed:', e);
            setSaveError(true);
        } finally {
            setIsSaving(false);
        }
    };

    if (isEditing) {
        return (
            <Box padding={{ vertical: 's', horizontal: 's' }}>
                <SpaceBetween size="s">
                    <RichTextEditor value={draft} onChange={setDraft} />
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} loading={isSaving}>Save</Button>
                        </SpaceBetween>
                    </Box>
                    {saveError && (
                        <Box color="text-status-error" fontSize="body-s">Failed to save. Please try again.</Box>
                    )}
                </SpaceBetween>
            </Box>
        );
    }

    return (
        <div style={{ marginTop: '-8px', marginBottom: '-8px' }}>
        <Container>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: '-10px 0',
            }}>
                <div style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>{sanitized ? parse(sanitized) : null}</div>
                {canEdit && (
                    <Button variant="icon" iconName="edit" onClick={handleEdit} ariaLabel="Edit content" />
                )}
            </div>
        </Container>
        </div>
    );
}

export default RichTextCommentEditor;
